import Alpine from "alpinejs";
import { Swiper } from "swiper";
import { Navigation, Autoplay } from "swiper/modules";

// PLUGINS
import collapse from "@alpinejs/collapse";
import focus from "@alpinejs/focus";
import intersect from "@alpinejs/intersect";
import morph from "@alpinejs/morph";
import ui from "@alpinejs/ui";
import dialog from "./plugins/dialog";

Alpine.plugin(collapse);
Alpine.plugin(focus);
Alpine.plugin(intersect);
Alpine.plugin(morph);
Alpine.plugin(ui);
Alpine.plugin(dialog);

import history from "./plugins/history";
import share from "./plugins/share";

Alpine.plugin(history);
Alpine.plugin(share);

// STORES
import cart from "./stores/cart";
Alpine.store("cart", cart);

// COMPONENTS
import media from "./components/media";
Alpine.data("media", media);

import product from "./components/product";
Alpine.data("product", product);

import quote from "./components/quote";
Alpine.data("quote", quote);

Alpine.store("menu", {
	open: false,
	openMobile: false,
});

Alpine.store("sticky", {
	is: false,
});

import productRecommendations from "./components/productRecommendations";
Alpine.data("productRecommendations", productRecommendations);

import numberInput from "./components/numberInput";
Alpine.data("numberInput", numberInput);

import validation from "./components/validation";
Alpine.data("validation", validation);

window.Alpine = Alpine;

function initQuoteBadgeSync() {
	let lastCount = -1;

	function sync() {
		const widget = document.querySelector(".grfq-quote-widget-container");
		const count = widget
			? parseInt(widget.getAttribute("data-number-of-items") || "0", 10)
			: 0;

		// Guard: skip DOM write if unchanged — prevents observer re-triggering itself
		if (count === lastCount) return;
		lastCount = count;

		document.querySelectorAll(".cart-qty").forEach((span) => {
			span.textContent = count > 0 ? `(${count})` : "";
		});
	}

	sync();

	new MutationObserver(sync).observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ["data-number-of-items"],
	});
}

document.addEventListener("DOMContentLoaded", function () {
	initQuoteBadgeSync();
	// Initialize brand links slider
	const brandsSlider = document.querySelector(".collection-links-slider");
	if (brandsSlider) {
		new Swiper(brandsSlider, {
			modules: [Navigation, Autoplay],
			slidesPerView: 1,
			spaceBetween: 0,
			autoplay: {
				delay: 4000,
			},
			loop: true,
			navigation: {
				nextEl: ".swiper-brands-button-next",
				prevEl: ".swiper-brands-button-prev",
			},
			breakpoints: {
				320: {
					slidesPerView: 2,
				},
				640: {
					slidesPerView: 3,
				},
				768: {
					slidesPerView: 4,
				},
				1024: {
					slidesPerView: 6,
				},
			},
		});
	}

	// Initialize testimonials slider
	const testimonialsSlider = document.querySelector(".testimonials-slider");
	if (testimonialsSlider) {
		new Swiper(testimonialsSlider, {
			modules: [Navigation],
			slidesPerView: 2,
			spaceBetween: 30,
			autoplay: false,
			loop: true,
			direction: "vertical",
			navigation: {
				nextEl: ".testimonials-next",
				prevEl: ".testimonials-prev",
			},
			breakpoints: {
				320: {
					slidesPerView: 2,
					autoplay: false,
					direction: "vertical",
				},
				640: {
					slidesPerView: 2,
					autoplay: false,
					direction: "vertical",
				},
				768: {
					slidesPerView: 2,
					autoplay: false,
					direction: "vertical",
				},
				1024: {
					slidesPerView: 2,
					direction: "horizontal",
					spaceBetween: 0,
				},
			},
		});
	}

	// Initialize Collection Listing Slider for mobile screens only
	const categorySlider = document.querySelector(".category-slider");
	const isSlides =
		document.querySelectorAll(".category-slider .swiper-wrapper li").length !==
		0;
	if (categorySlider) {
		const slides = categorySlider.querySelectorAll("li"); // Select all slide elements
		const nextButton = document.querySelector(".category-next");
		const prevButton = document.querySelector(".category-prev");
		const categorySliderWrapper = document.querySelector(
			".category-slider-wrapper"
		);

		const initializeSwiper = () => {
			if (window.innerWidth <= 768) {
				// Mobile screens (768px and below)
				slides.forEach((slide) => slide.classList.add("swiper-slide")); // Add .swiper-slide class
				if (!categorySlider.swiper) {
					// Check if Swiper is not already initialized
					new Swiper(categorySlider, {
						modules: [Navigation, Autoplay],
						slidesPerView: 1.2, // Show part of the next slide
						spaceBetween: 20, // Add space between slides
						autoplay: {
							delay: 4000,
						},
						loop: true,
						navigation: {
							nextEl: ".category-next",
							prevEl: ".category-prev",
						},
						breakpoints: {
							420: {
								slidesPerView: 1.6,
							},
							470: {
								slidesPerView: 2.2,
							},
						},
					});
				}
				// Show navigation buttons on mobile
				if (isSlides) {
					if (nextButton) nextButton.style.display = "block";
					if (prevButton) prevButton.style.display = "block";
					if (categorySliderWrapper)
						categorySliderWrapper.style.display = "block";
				}
			} else {
				// Desktop screens (>768px)
				slides.forEach((slide) => slide.classList.remove("swiper-slide")); // Remove .swiper-slide class
				if (categorySlider.swiper) {
					// Destroy Swiper if initialized on larger screens
					categorySlider.swiper.destroy(true, true);
				}
				// Hide navigation buttons on desktop
				if (nextButton) nextButton.style.display = "none";
				if (prevButton) prevButton.style.display = "none";
			}
		};

		// Initialize Swiper on page load
		initializeSwiper();

		// Reinitialize Swiper on window resize
		window.addEventListener("resize", initializeSwiper);
	}

	// Initialize featured collection slider(s)
	const featuredCollectionSliders = document.querySelectorAll(
		".featured-collection-slider"
	);
	featuredCollectionSliders.forEach((featuredCollectionSlider) => {
		const parent = featuredCollectionSlider.closest("section");
		const nextButton = parent?.querySelector(".featured-collection-button-next");
		const prevButton = parent?.querySelector(".featured-collection-button-prev");

		const swiper = new Swiper(featuredCollectionSlider, {
			modules: [Navigation, Autoplay],
			slidesPerView: 1,
			spaceBetween: 30,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false,
			},
			loop: true,
			navigation: {
				nextEl: nextButton,
				prevEl: prevButton,
			},
			breakpoints: {
				768: {
					slidesPerView: 2,
					spaceBetween: 30,
				},
				1024: {
					slidesPerView: 4,
					spaceBetween: 30,
				},
			},
			on: {
				init: function () {
					// Remove grid classes and show slider
					const wrapper =
						featuredCollectionSlider.querySelector(".swiper-wrapper");
					if (wrapper) {
						wrapper.classList.remove(
							"grid",
							"grid-cols-1",
							"md:grid-cols-2",
							"lg:grid-cols-4",
							"gap-6"
						);
					}
					// Fade in slider and navigation
					featuredCollectionSlider.style.opacity = "1";
					if (prevButton) prevButton.style.opacity = "1";
					if (nextButton) nextButton.style.opacity = "1";
				},
			},
		});
	});
});

// INIT
Alpine.start();
